import { useEffect, useState } from "react";
import { useAppDispatch } from "../store/hooks";
import ConstraintService from "../service/constraintService";
import { CategoryImpact } from "../types/constraint";

/**
 * Backs the delete-category confirmation: shows the derived count instantly, then swaps in the
 * authoritative backend count when it lands. A failed or superseded fetch is ignored — the
 * derived numbers stay on screen rather than showing a spinner or an error the planner can't
 * act on.
 */
const useCategoryImpact = (
  derivedImpact: CategoryImpact,
  categoryId: string | null,
): { impact: CategoryImpact } => {
  const dispatch = useAppDispatch();
  const [impact, setImpact] = useState<CategoryImpact>(derivedImpact);

  useEffect(() => {
    setImpact(derivedImpact);
    if (categoryId === null) return;

    let isCurrent = true;
    ConstraintService.getCategoryImpact(categoryId, dispatch)
      .then((fetchedImpact) => {
        if (isCurrent) setImpact(fetchedImpact);
      })
      .catch(() => {});

    return () => {
      isCurrent = false;
    };
    // derivedImpact intentionally excluded: it is only meant to seed the display when
    // categoryId changes (i.e. the dialog opens for a different category), not on every
    // Redux update while the dialog is already open, which would re-trigger the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, dispatch]);

  return { impact };
};

export default useCategoryImpact;
